import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import default_theme from 'assets/themes/themes';
import { loginRequest, clearError } from '../../redux/auth/authSlice';


const LoginPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { loading, error, isAuthenticated } = useSelector((state) => state.auth);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
    }
    return () => {
      dispatch(clearError());
    };
  }, [location.state, dispatch]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch(loginRequest({ email, password }));
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 8, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Login
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
          Sign in to your account
        </Typography>

        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
            {successMessage}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            margin="normal"
            autoComplete="email"
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: default_theme.palette.other.figmaBlue,
                },
                '&:hover fieldset': {
                  borderColor: default_theme.palette.other.figmaBlue,
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'text.primary',
                  borderWidth: 1,
                },
              },

              "& .MuiInputLabel-root": {
                color: default_theme.palette.other.figmaBlue,
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: default_theme.palette.other.figmaBlue,
              },
            }}
          />
          <TextField
            fullWidth
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            margin="normal"
            autoComplete="current-password"
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: default_theme.palette.other.figmaBlue,
                },
                '&:hover fieldset': {
                  borderColor: default_theme.palette.other.figmaBlue,
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'text.primary',
                  borderWidth: 1,
                },
              },

              "& .MuiInputLabel-root": {
                color: default_theme.palette.other.figmaBlue,
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: default_theme.palette.other.figmaBlue,
              },
            }}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Login'}
          </Button>
          <Box textAlign="center" sx={{ mt: 2 }}>
            <Link to="/forgot-password" style={{ textDecoration: 'none', display: 'block', mb: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.primary',
                  '&:hover': {
                    textDecoration: 'underline',
                    color: 'text.primary'
                  }
                }}
              >
                Forgot your password?
              </Typography>
            </Link>
            <Link to="/register" style={{ textDecoration: 'none' }}>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.primary',
                  '&:hover': {
                    textDecoration: 'underline',
                    color: 'text.primary'
                  }
                }}
              >
                Don't have an account? Register here
              </Typography>
            </Link>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default LoginPage;
